import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import { useTranslation } from 'react-i18next';
import DaumZipComponent from './DaumZipComponent';
import useZipCode from '../hooks/useZipCode';
import { mediaQueries } from '../styles/mediaQueries';
import LocationSelector from './LocationSelector';
import type { PostalCodeLocation } from '../services/state/model';
import { FORM_FIELD_MIN_WIDTH } from '../styles/constants';

interface StyledContainerProps {
  $isAltView: boolean;
}

export interface ZipCodeProps {
  name: string;
  defaultValue: string;
  isValid: boolean;
  isAltView: boolean;
  setIsValid: (value: boolean) => void;
  customRef: React.RefObject<HTMLInputElement | null>;
  postalCodeLocations: PostalCodeLocation[];
}

const DaumFieldContainer = styled.div<StyledContainerProps>`
  min-height: 6.5rem;
  max-width: ${(props: StyledContainerProps) =>
    props.$isAltView ? '100%' : 'max-content'};
`;

const FormFieldContainer = styled.div<StyledContainerProps>`
  min-height: 6.5rem;
  ${mediaQueries.portraitTabletUp} {
    max-width: ${(props: StyledContainerProps) =>
      props.$isAltView ? '100%' : FORM_FIELD_MIN_WIDTH};
  }
`;

const ZipCode: React.FC<ZipCodeProps> = ({
  name,
  defaultValue,
  isValid,
  isAltView,
  setIsValid,
  customRef,
  postalCodeLocations,
}) => {
  const [{ newZipCode }, { handleBlur, handleChange }] = useZipCode(
    defaultValue,
    setIsValid,
  );
  const { t } = useTranslation();
  const { country } = CSS_CONFIG;

  useEffect(() => {
    if (!isValid && customRef.current) {
      customRef.current.focus();
    }
  }, [customRef, isValid]);

  return country === 'kr' ? (
    <DaumFieldContainer $isAltView={isAltView}>
      <DaumZipComponent />
    </DaumFieldContainer>
  ) : (
    <FormFieldContainer $isAltView={isAltView}>
      <FormField
        valid={isValid}
        validation={{
          msg: t('components.zipCode.errorMessage'),
          role: 'alert',
          'aria-live': 'assertive',
          id: `ZipCodeTextInputFieldError`,
          type: 'error',
        }}
        shouldValidate={!isValid}
      >
        <InputField
          id={name}
          type="text"
          value={newZipCode}
          label={t('components.zipCode.label')}
          onChange={handleChange}
          onBlur={handleBlur}
          autoComplete="postal-code"
          ref={customRef}
          required
        />
        <LocationSelector locations={postalCodeLocations} />
      </FormField>
    </FormFieldContainer>
  );
};

export default ZipCode;
